import {
  ForwardRefExoticComponent,
  RefAttributes,
  SVGProps,
  useEffect,
  useState,
  useRef,
} from "react";
import {
  CheckIcon,
  ChevronRightIcon,
  UserGroupIcon,
  UserIcon,
  XMarkIcon,
  ClipboardDocumentListIcon,
  LinkIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
} from "@heroicons/react/20/solid";
import { CalendarDaysIcon, InboxIcon } from "@heroicons/react/24/outline";
import { Candidate, CandidateStatus, Role, RoleStatus } from "../../Models";

import {
  GetInterviewSlots,
} from "../../api/interviewPanel.api";

import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { RoleStatusType, dateConverter } from "../../Helpers";
import useApiToken from "../../hooks/useApiToken";
import { useMsal } from "@azure/msal-react";
import ConfirmModal from "../Shared/ConfirmModal";
import CandidateNotes from "./CandidateNotes";
import CandidateDetails from "./CandidateDetails";
import { NIL } from "uuid";
import { useNotification } from "../../context/NotificationContext";
import { AvailableSlot } from "../Roles/RoleCandidates/BulkActionModalDescription";
import ScoreGrades from "./ScoreGrades";
import { AssessmentUrl } from "../../config/appConfig";
import Interviews from "./Interviews";
import {
  CandidateCvFlagResult,
  CreateCandidateStatus,
  GenerateCandidateCvFlagResult,
  GetCandidate,
  GetCandidateCvFlagResult,
  GetCandidateStatus,
  GetRoleForCandidate,
  GetCandidateInterviewAnswers,
  GetInterviewSlotDetails,
  GetCandidateRoleTimeline,
  GetResetJourneyEligibility,
  ResetCandidateJourney,
} from "../../api/candidates.api";
import {
  checkAvailableSlots,
  GetStatusForRole,
  refreshRoleCandidateCache,
  GetAvailableAssessmentSlots,
  AvailableAssessmentSlot,
  GetAvailableInterviewSlots,

} from "../../api/roles.api";
import { getAllInterviewAssessments } from "../../api/assessmentquestionnaires.api";
import { AssessmentDetailsModel } from "../Assessment/AssessmentQuestions";
import InterviewControlPanel from "../Shared/Modals/InterviewControlPanel";
import InterviewScorePanel, {
  InterviewSession,
} from "../Shared/Modals/InterviewScorePanel";
import { last } from "@amcharts/amcharts4/.internal/core/utils/Array";
import { format } from "date-fns";
import { getActiveSlotForCandidate, resetCandidateWindow, sendAssessmentLinkWithoutSeb } from "../../api/assessmentslots.api";
import {
  getCandidateExamSessions,
  revokeCandidateExamSessions,
  ExamSessionsResponse,
} from "../../api/examsession.api";
import ResetJourneyModal from "../Roles/RoleCandidates/ResetJourneyModal";
import RebookAssessmentSlotModal from "./RebookAssessmentSlotModal";

type eventType = {
  icon: ForwardRefExoticComponent<
    Omit<SVGProps<SVGSVGElement>, "ref"> & {
      title?: string | undefined;
      titleId?: string | undefined;
    } & RefAttributes<SVGSVGElement>
  >;
  bgColorClass: string;
};
type timeline = {
  id: string;
  content: string;
  type: eventType;
  target: string;
  date: string;
};
type advanceWithEmailOption = { name: string; description: string };
type disqualifyWithEmailOption = { name: string; description: string };

const eventTypes = {
  applied: { icon: UserIcon, bgColorClass: "bg-entntblue" },
  completed: { icon: CheckIcon, bgColorClass: "bg-entntorange" },
  notCompleted: { icon: XMarkIcon, bgColorClass: "bg-gray-400" },
};

function ToggleSwitch({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label className="relative inline-flex items-center cursor-pointer">
      <input type="checkbox" className="sr-only peer" checked={checked} onChange={onChange} />
      <div className="w-9 h-5 rounded-full bg-gray-200 dark:bg-gray-700 peer-checked:bg-entntblue transition-colors duration-200" />
      <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 peer-checked:translate-x-4" />
    </label>
  );
}

const advanceWithEmailOptions: advanceWithEmailOption[] = [
  {
    name: "Advance with Email Notification",
    description:
      "Candidate will be advanced to the next steps, and an email notification will be sent to them regarding the next steps.",
  },
  {
    name: "Advance without Email Notification",
    description:
      "Candidate will be advanced to the next round without sending any email notification.",
  },
];

const disqualifyWithEmailOptions: disqualifyWithEmailOption[] = [
  {
    name: "Disqualify with Email Notification",
    description:
      "Candidate will be disqualified and an email notification will be sent to them regarding the decision.",
  },
  {
    name: "Disqualify without Email Notification",
    description:
      "Candidate will be disqualified without sending any email notification.",
  },
];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

// Map icon names from backend to actual icon components
const iconMap: Record<string, React.ElementType> = {
  UserIcon,
  CheckIcon,
  XMarkIcon,
};
export interface AssessmentDetailsModelWithId extends AssessmentDetailsModel {
  id: string;
}

export default function CandidatePage() {
  const [candidate, setCandidate] = useState<Candidate>();
  const [candidateRole, setCandidateRole] = useState<Role[]>([]);
  const { candidateId, roleId } = useParams();
  const navigate = useNavigate();
  const [disqualifyConfirmModal, setDisqualifyConfirmModal] = useState(false);
  const [advanceStatusConfirmModal, setAdvanceStatusConfirmModal] =
    useState(false);
  const [candidateStatus, setCandidateStatus] = useState<CandidateStatus[]>([]);
  const [roleStatus, setRoleStatus] = useState<RoleStatus[]>([]);
  const [timeline, setTimeline] = useState<timeline[]>([]);
  const [latestRoleStatus, setLatestRoleStatus] = useState<RoleStatus>();
  const [eventTypes, setEventTypes] = useState<any>({});
  const [disqualifiedOrHired, setDisqualifiedOrHired] =
    useState<RoleStatus | null>(null);
  const [advanceWithEmail, setAdvanceWithEmail] = useState(
    advanceWithEmailOptions[0]
  );
  const [disqualifyWithEmail, setDisqualifyWithEmail] = useState(
    disqualifyWithEmailOptions[0],
  );
  const [advanceStatusSpinner, setAdvanceStatusSpinner] = useState(false);
  const { info, error, success } = useNotification();
  const { instance } = useMsal();
  const token = useApiToken();
  const [nextTimelineIndex, setNextTimelineIndex] = useState<number>(1);
  const [includeSlot, setIncludeSlot] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<AvailableSlot[]>([]);
  const [panelGroups, setPanelGroups] = useState<{ panel: string; count: number }[]>([]);
  const [selectedPanels, setSelectedPanels] = useState<string[]>([]);
  const [assignMode, setAssignMode] = useState<"candidate" | "recruiter">("candidate");
  const [detailedAssessmentSlots, setDetailedAssessmentSlots] = useState<AvailableAssessmentSlot[]>([]);
  const [detailedInterviewSlots, setDetailedInterviewSlots] = useState<any[]>([]);
  const [selectedAssessmentSlotId, setSelectedAssessmentSlotId] = useState<string>("");
  const [selectedInterviewSlotId, setSelectedInterviewSlotId] = useState<string>("");
  const [interviewAssessments, setInterviewAssessments] = useState<
    AssessmentDetailsModelWithId[]
  >([]);
  const [selectedAssessment, setSelectedAssessment] =
    useState<AssessmentDetailsModelWithId | null>(null);
  const [isInterviewQuestionModalOpen, setIsInterviewQuestionModalOpen] =
    useState(false);
  const [interviewId, setInterviewId] = useState<string | null>(null);
  const [candidateInterviewScoreOpen, setCandidateInterviewScoreOpen] =
    useState(false);
  const [interviewSession, setInterviewSession] =
    useState<InterviewSession | null>(null);
  const [activeInterviewId, setActiveInterviewId] = useState<string | null>(
    null,
  );
  const [interviewIds, setInterviewIds] = useState<{
    [timelineId: string]: string;
  }>({});
  const [showCopyLinkNotification, setShowCopyLinkNotification] =
    useState(false);
  const [cvFlagResult, setCvFlagResult] =
    useState<CandidateCvFlagResult | null>(null);
  const [isFetchingCvFlags, setIsFetchingCvFlags] = useState(false);
  const [cvFlagError, setCvFlagError] = useState<string | null>(null);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const disqualifiedIndex = timeline.findIndex(t => t.content === "Disqualified");

  // Assessment Window Reset state
  const [activeAssessmentSlot, setActiveAssessmentSlot] = useState<{
    assessmentSlotId: string;
    startTime: string;
    endTime: string;
    originalEndTime: string;
    windowWasReset: boolean;
    takenWithoutSeb?: boolean;
    roleStatusId?: string | null;
    assessmentQuestionnaireId?: string | null;
    assessmentName?: string | null;
    directLink?: string;
    sebLink?: string;
  } | null>(null);
  const [isResettingWindow, setIsResettingWindow] = useState(false);
  const [windowResetSuccess, setWindowResetSuccess] = useState(false);
  const [isSendingWithoutSeb, setIsSendingWithoutSeb] = useState(false);
  const [sentWithoutSebSuccess, setSentWithoutSebSuccess] = useState(false);
  const [isRebookSlotModalOpen, setIsRebookSlotModalOpen] = useState(false);
  const [rebookSuccess, setRebookSuccess] = useState(false);

  // Live exam session state — which browser currently holds this candidate's assessment.
  const [examSessions, setExamSessions] = useState<ExamSessionsResponse | null>(null);
  const [isEndingSession, setIsEndingSession] = useState(false);

  // Reset Candidate Journey state
  const [resetJourneyEligibility, setResetJourneyEligibility] = useState<any>(null);
  const [isResetJourneyModalOpen, setIsResetJourneyModalOpen] = useState(false);

  useEffect(() => {
    // Check if candidate is disqualified
    const hasRejectedStatus = timeline.some((item) => {
      const typeKey =
        typeof item.type === "string" ? item.type : (item.type as any)?.name;
      const typeMetadata = eventTypes[typeKey] || {};
      return typeMetadata.icon === "XMarkIcon";
    });
    setIsDisqualified(hasRejectedStatus);
  }, [timeline, eventTypes]);

  useEffect(() => {
    const lastCompletedIndex = timeline.reduce((lastIndex, item, index) => {
      const typeKey =
        typeof item.type === "string" ? item.type : (item.type as any)?.name;
      const typeMetadata = eventTypes[typeKey] || {};
      const IconComponent = iconMap[typeMetadata.icon];
      if (IconComponent === CheckIcon) {
        return index;
      }
      return lastIndex;
    }, 0);
    if (timeline[lastCompletedIndex + 1]?.content === "Manual Evaluation") {
      setNextTimelineIndex(lastCompletedIndex + 2);
      return;
    }
    setNextTimelineIndex(lastCompletedIndex + 1);
  }, [timeline, eventTypes]);

  useEffect(() => {
    if (!candidateId || !roleId || !token) return;
    GetCandidateRoleTimeline(roleId, candidateId, token)
      .then((data) => {
        setTimeline(data.timeline);
        setEventTypes(data.eventTypes);
      })
      .catch((err) => {
        console.error("Error fetching candidate timeline:", err);
      });
      
    // Fetch Journey Reset eligibility
    GetResetJourneyEligibility(candidateId, roleId, token)
      .then((data) => {
        setResetJourneyEligibility(data);
      })
      .catch((err) => {
        console.error("Error fetching reset eligibility:", err);
        setResetJourneyEligibility(null);
      });
  }, [candidateId, roleId, token, candidateStatus]);

  useEffect(() => {
    if (candidateId === undefined || token === undefined) return;
    GetCandidate(candidateId, token).then((cd) => {
      setCandidate(cd);
    });
    GetRoleForCandidate(candidateId, token).then((rl) => {
      const filteredRoles = rl.filter((role) => role.id === roleId);
      setCandidateRole(filteredRoles);
    });
    GetCandidateStatus(candidateId, token).then((cs) => {
      setCandidateStatus(cs.filter((c) => c.roleId === roleId));
    });

    if (roleId !== undefined)
      GetStatusForRole(roleId, token).then((sr) =>
        setRoleStatus(sr.roleStatuses.sort((a, b) => a.order - b.order)),
      );

    // fetchInterviewSlotDetails();
  }, [candidateId, roleId, token, instance]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // useEffect(() => {
  //   const timeline: timeline[] = [];
  //   let latestStatusId = NIL;

  //   timeline.push({
  //     id: NIL,
  //     type: eventTypes.applied,
  //     content: RoleStatusType.applied,
  //     target: "",
  //     date: "",
  //   });

  //   roleStatus.forEach((rs) => {
  //     const correspondingCandidateStatus = candidateStatus.find((cs) => {
  //       return cs.roleStatusId === rs.id;
  //     });
  //     const isDisqualifiedOrHired = rs.order === roleStatus.length - 1;
  //     if (correspondingCandidateStatus) {
  //       if (isDisqualifiedOrHired) setDisqualifiedOrHired(rs);
  //       const formattedDate = dateConverter(
  //         correspondingCandidateStatus.date,
  //         true
  //       );
  //       timeline.push({
  //         id: rs.id,
  //         type: eventTypes.completed,
  //         content: rs.name,
  //         target: "",
  //         date: formattedDate,
  //       });
  //       latestStatusId = rs.id;
  //     } else {
  //       if (!isDisqualifiedOrHired)
  //         timeline.push({
  //           id: rs.id,
  //           type: eventTypes.notCompleted,
  //           content: rs.name,
  //           target: "",
  //           date: "",
  //         });
  //     }
  //   });
  //   if (timeline.length < roleStatus.length) {
  //     timeline.push({
  //       id: "0",
  //       type: eventTypes.notCompleted,
  //       content: RoleStatusType.hired + " / " + RoleStatusType.rejected,
  //       target: "",
  //       date: "",
  //     });
  //   }
  //   setLatestRoleStatus(roleStatus.find((rs) => rs.id === latestStatusId));
  //   setTimeline(timeline);
  // }, [candidateStatus, roleStatus]);

  useEffect(() => {
    let nextRoleStatus = null;

    if (latestRoleStatus) {
      nextRoleStatus = roleStatus.find(
        (rs) => rs.order === latestRoleStatus.order + 1,
      );

      // Enable slots if the next status has available slots or contains "interview" in name (backward compatibility)
      if (nextRoleStatus) {
        const hasSlots = availableSlots.some(s => s.roleStatusId === nextRoleStatus!.id && s.adjustedCount > 0);
        if (hasSlots || nextRoleStatus.name.toLowerCase().includes("interview")) {
          setIncludeSlot(true);
        }
      }
    }
  }, [latestRoleStatus, roleStatus, availableSlots]);

  useEffect(() => {
    if (!token) return;
    const fetchAvailableSlots = async () => {
      try {
        // Always fetch available slots to determine if we should enable the option
        // if (!includeSlot) {
        //   setAvailableSlots([]);
        //   setPanelGroups([]);
        //   setSelectedPanels([]);
        //   return;
        // }

        const slots = await checkAvailableSlots(roleId, token);
        setAvailableSlots(slots);

        // fetch and group interview slots by panel
        try {
          if (token && roleId) {
            const interviewSlots = await GetInterviewSlots(roleId, token);
            console.log("Interview slots fetched:", interviewSlots);

            const now = new Date();
            const panelMap = new Map<string, number>();
            (interviewSlots || []).forEach((s: any) => {
              // Only include slots that haven't ended yet
              if (new Date(s.endTime) >= now) {
                const panel = s.interviewerEmail || "Unassigned";
                panelMap.set(panel, (panelMap.get(panel) || 0) + 1);
              }
            });
            const groups = Array.from(panelMap.entries())
              .map(([panel, count]) => ({ panel, count }))
              .sort((a, b) => a.panel.localeCompare(b.panel));
            console.log("Panel groups:", groups);
            setPanelGroups(groups);
            // Debug: log includeSlot when fetching panels
            console.log("includeSlot at fetch:", includeSlot);
            // Auto-select first panel only if advancing to interview and no panel already selected
            if (groups.length > 0 && includeSlot && selectedPanels.length === 0) {
              setSelectedPanels([groups[0].panel]);
              console.log("Selected first panel:", groups[0].panel);
            }
          }
        } catch (inner) {
          console.error("Error fetching interview slots:", inner);
        }
      } catch (error) {
        console.error("Error fetching available slots:", error);
      }
    };

    fetchAvailableSlots();
    getInterviewAssessments();
  }, [roleId, token, includeSlot]);

  useEffect(() => {
    if (!token || !roleId) return;
    GetAvailableAssessmentSlots(roleId, token)
      .then((slots) => setDetailedAssessmentSlots(slots || []))
      .catch(() => setDetailedAssessmentSlots([]));
    GetAvailableInterviewSlots(token, roleId)
      .then((slots) => setDetailedInterviewSlots(slots || []))
      .catch(() => setDetailedInterviewSlots([]));
  }, [roleId, token]);

  // Auto-switch to recruiter mode when the next stage has pre-created slots.
  // This ensures recruiterAssignedSlotId is always set in the payload (new single-email flow).
  // Without this, assignMode stays "candidate" and the backend sends the old booking-link email.
  // Note: targetRoleStatusId is derived inline here because it is a let-variable declared later
  // in the component body, so it cannot be referenced from a useEffect at this position.
  useEffect(() => {
    let resolvedTargetId: string | null = null;
    let idx = nextTimelineIndex;
    while (idx < timeline.length) {
      const item = timeline[idx];
      const match = roleStatus.find((rs) => rs.id === item.id);
      if (match && item.id !== NIL) {
        resolvedTargetId = item.id;
        break;
      }
      idx++;
    }
    if (!resolvedTargetId) {
      const offered = roleStatus.find((rs) => rs.name === RoleStatusType.hired);
      if (offered) resolvedTargetId = offered.id;
    }

    const nextStageHasSlots =
      detailedAssessmentSlots.some((s) => s.roleStatusId === resolvedTargetId) ||
      detailedInterviewSlots.some((s: any) => s.roleStatusId === resolvedTargetId);

    setAssignMode(nextStageHasSlots ? "recruiter" : "candidate");
  }, [detailedAssessmentSlots, detailedInterviewSlots, timeline, nextTimelineIndex, roleStatus]);

  useEffect(() => {
    if (activeInterviewId) {
      getCandidateInterviewAnswers();
    }
  }, [activeInterviewId]);

  useEffect(() => {
    // Fetch interview IDs for interview events in timeline
    if (timeline.length > 0 && candidateId && roleId) {
      const fetchInterviewIds = async () => {
        const newInterviewIds: { [timelineId: string]: string } = {};
        for (const timelineItem of timeline) {
          if (timelineItem.content.toLowerCase() === "interview slot booked") {
            try {
              const res = await GetInterviewSlotDetails(
                candidateId!,
                roleId!,
                timelineItem.id,
              );
              if (res?.id) {
                newInterviewIds[timelineItem.id] = res.id;
              }
            } catch (error) {
              console.log(
                "Error fetching interview for timeline item:",
                timelineItem.id,
                error,
              );
            }
          }
        }

        setInterviewIds(newInterviewIds);

        // Set the first interview as active if no active interview is set
        if (!activeInterviewId && Object.keys(newInterviewIds).length > 0) {
          const firstInterviewId = Object.values(newInterviewIds)[0];
          setActiveInterviewId(firstInterviewId);
        }
      };

      fetchInterviewIds();
    }
  }, [timeline, candidateId, roleId, activeInterviewId]);

  const getCandidateInterviewAnswers = async () => {
    if (!candidateId || !activeInterviewId) return;
    const answers = await GetCandidateInterviewAnswers(
      candidateId,
      activeInterviewId,
    );
    setInterviewSession(answers || null);
  };

  // const fetchInterviewSlotDetails = async () => {
  //   if (!candidateId || !roleId) return;
  //   try {
  //     const res = await GetInterviewSlotDetails(candidateId!, roleId!);
  //     if (res?.assessmentQuestionnaireId) {
  //       const assessment = interviewAssessments?.find(
  //         (a) => a.id === res.assessmentQuestionnaireId,
  //       );
  //       setSelectedAssessment(assessment || null);
  //     }
  //     setInterviewId(res?.id || null);
  //   } catch (error) {
  //     console.log(error);
  //   }
  // };

  const isNextStatusValid = (
    nextStatus: CandidateStatus,
    slotBoolean: boolean,
  ): boolean => {
    if (slotBoolean) {
      if (availableSlots.length === 0) {
        return false;
      }

      const matchedSlot = availableSlots.find(
        (slot) => slot.roleStatusId === nextStatus.roleStatusId,
      );

      return matchedSlot !== undefined && matchedSlot.adjustedCount > 0;
    } else {
      return true;
    }
  };

  const getInterviewAssessments = async () => {
    if (!token) return;
    try {
      const res = await getAllInterviewAssessments(token);
      setInterviewAssessments(res || []);
    } catch (error) {
      console.log(error);
    }
  };

  const handleDisqualify = () => {
    const disqualified = roleStatus.find(
      (el) => el.name === RoleStatusType.rejected,
    );
    if (!roleId || !candidateId || !disqualified || !token) return;
    const newCandidateStatus: CandidateStatus = {
      roleId: roleId,
      candidateId: candidateId,
      roleStatusId: disqualified.id,
      date: new Date().toISOString(),
      email: advanceWithEmail.name === advanceWithEmailOptions[0].name,
      slots: false,
    };

    CreateCandidateStatus(token, newCandidateStatus).then((cs) => {
      setCandidateStatus([...candidateStatus, cs]);
      setDisqualifyConfirmModal(false);
      refreshRoleCandidateCache(roleId, token);
    });
  };
  let nextStatusIndex = 0;
  if (latestRoleStatus)
    nextStatusIndex = roleStatus.indexOf(latestRoleStatus) + 1;

  //if next status is either disqualied or offered
  if (nextStatusIndex >= roleStatus.length - 2) nextStatusIndex = -1;

  // Single source of truth for "what status is this candidate actually advancing to" —
  // used both to decide which slot picker to show AND what gets submitted, so the two can never disagree.
  let targetTimelineIndex = nextTimelineIndex;
  let targetRoleStatusId: string | null = null;
  while (targetTimelineIndex < timeline.length) {
    const timelineItem = timeline[targetTimelineIndex];
    const matchingRoleStatus = roleStatus.find((rs) => rs.id === timelineItem.id);
    if (matchingRoleStatus && timelineItem.id !== NIL) {
      targetRoleStatusId = timelineItem.id;
      break;
    }
    targetTimelineIndex++;
  }
  if (!targetRoleStatusId) {
    const offeredStatus = roleStatus.find((rs) => rs.name === RoleStatusType.hired);
    if (offeredStatus) {
      targetRoleStatusId = offeredStatus.id;
    }
  }

  const needsAssessmentSlot = targetRoleStatusId
    ? detailedAssessmentSlots.some((s) => s.roleStatusId === targetRoleStatusId)
    : false;
  const matchingInterviewSlotsForNext = targetRoleStatusId
    ? detailedInterviewSlots.filter((s: any) => s.roleStatusId === targetRoleStatusId)
    : [];
  // Only show the slot-mode picker when the *next* stage specifically has pre-created slots.
  // Checking all slots (any stage) caused the picker to appear even when no valid slot existed
  // for the target stage, leaving recruiterAssignedSlotId unset → old booking-link email fired.
  const hasDetailedSlots = needsAssessmentSlot || matchingInterviewSlotsForNext.length > 0;

  // The interview-slot picker (panel selection / available-slots card / "send slots" toggle) is only
  // relevant when the candidate is actually moving into the Interview stage next — otherwise it showed
  // up (with 0 available) while advancing into unrelated stages like Assessment.
  const targetRoleStatus = targetRoleStatusId
    ? roleStatus.find((rs) => rs.id === targetRoleStatusId)
    : undefined;
  const isAdvancingToInterview = !!targetRoleStatus?.name.toLowerCase().includes("interview");

  // "Advance without Email Notification" should never require a slot to be picked — there's no
  // email/booking link being sent, so slot assignment doesn't apply at all in that path.
  const isEmailAdvance = advanceWithEmail.name === advanceWithEmailOptions[0].name;

  const handleAdvanceToNextStatus = () => {

    if (!roleId || !candidateId || !token) return;

    const roleStatusIdToSubmit = targetRoleStatusId;
    if (!roleStatusIdToSubmit) {
      setAdvanceStatusConfirmModal(false);
      return;
    }

    const needsAssessmentForTarget = needsAssessmentSlot;
    const matchingInterviewForTarget = matchingInterviewSlotsForNext;

    if (isEmailAdvance && assignMode === "recruiter") {
      if (needsAssessmentForTarget && !selectedAssessmentSlotId) {
        setAdvanceStatusConfirmModal(false);
        info("Error", "Please select an assessment slot.");
        return;
      }
      if (!needsAssessmentForTarget && matchingInterviewForTarget.length > 0 && !selectedInterviewSlotId) {
        setAdvanceStatusConfirmModal(false);
        info("Error", "Please select an interview slot.");
        return;
      }
    }

    const nextStatus: CandidateStatus = {
      roleId: roleId,
      candidateId: candidateId,
      roleStatusId: roleStatusIdToSubmit,
      date: new Date().toISOString(),
      email: isEmailAdvance,
      slots: isEmailAdvance ? (assignMode === "recruiter" ? false : includeSlot) : false,
      panel:
        isEmailAdvance && assignMode === "candidate" && includeSlot && selectedPanels && selectedPanels.length > 0
          ? selectedPanels.join(",")
          : undefined,
      recruiterAssignedSlotId:
        isEmailAdvance && assignMode === "recruiter"
          ? needsAssessmentForTarget
            ? selectedAssessmentSlotId
            : matchingInterviewForTarget.length > 0
              ? selectedInterviewSlotId
              : undefined
          : undefined,
      recruiterAssignedSlotType:
        isEmailAdvance && assignMode === "recruiter"
          ? needsAssessmentForTarget
            ? "assessment"
            : matchingInterviewForTarget.length > 0
              ? "interview"
              : undefined
          : undefined,
    };
    if (isEmailAdvance && assignMode === "candidate" && !isNextStatusValid(nextStatus, includeSlot)) {
      setAdvanceStatusConfirmModal(false);
      info("Error", "No available slots to send.");
      return;
    }
    setAdvanceStatusSpinner(true);
    setAdvanceStatusConfirmModal(false);



    CreateCandidateStatus(token, nextStatus).then((cs) => {
      setCandidateStatus([...candidateStatus, cs]);
      setAdvanceStatusSpinner(false);
      refreshRoleCandidateCache(roleId, token);
      setAssignMode("candidate");
      setSelectedAssessmentSlotId("");
      setSelectedInterviewSlotId("");
      // Check if next timeline item is Manual Evaluation, skip it by incrementing by 2
      const nextIndex = targetTimelineIndex + 1;
      if (
        nextIndex < timeline.length &&
        timeline[nextIndex].content === "Manual Evaluation"
      ) {
        setNextTimelineIndex(nextIndex + 1);
      } else {
        setNextTimelineIndex(nextIndex);
      }
      info("Candidate Status", "Candidate status has changed successfully");
      refreshRoleCandidateCache(roleId, token);
    }).catch((err) => {
      console.error("Error advancing candidate:", err);
      setAdvanceStatusSpinner(false);
      error("Error", err instanceof Error ? err.message : "Failed to advance candidate status");
    });
  };

  const handleToggleSlot = () => {
    setIncludeSlot((prev) => {
      const next = !prev;
      // If turning slots off, clear selected panel immediately to avoid stale value
      if (!next) setSelectedPanels([]);
      return next;
    });
  };

  const handleGetCvFlags = async () => {
    if (!candidateId || !roleId || !token) return;

    setIsFetchingCvFlags(true);
    setCvFlagError(null);
    try {
      const result = await GenerateCandidateCvFlagResult(
        roleId,
        candidateId,
        token,
      );
      setCvFlagResult(result);
    } catch (error: any) {
      setCvFlagError(error?.message || "Failed to fetch CV flags");
    } finally {
      setIsFetchingCvFlags(false);
    }
  };

  useEffect(() => {
    if (!candidateId || !roleId || !token) return;

    let isCancelled = false;

    const fetchCvFlagsOnOpen = async () => {
      setIsFetchingCvFlags(true);
      setCvFlagError(null);
      try {
        const result = await GetCandidateCvFlagResult(roleId, candidateId, token);
        if (!isCancelled) {
          setCvFlagResult(result ?? null);
        }
      } catch (error: any) {
        if (!isCancelled) {
          setCvFlagError(error?.message || "Failed to fetch CV flags");
        }
      } finally {
        if (!isCancelled) {
          setIsFetchingCvFlags(false);
        }
      }
    };

    fetchCvFlagsOnOpen();

    return () => {
      isCancelled = true;
    };
  }, [candidateId, roleId, token]);

  // Load active assessment slot for the window-reset card.
  // Pass roleId so the backend returns the slot for the candidate's CURRENT stage only.
  // If the candidate has advanced to Interview (or any non-assessment stage) the backend
  // returns 404 and slot will be null, hiding the reset card automatically.
  useEffect(() => {
    if (!candidateId || !token) return;
    getActiveSlotForCandidate(candidateId, token, roleId).then(slot => {
      setActiveAssessmentSlot(slot);
    });
  }, [candidateId, roleId, token, candidateStatus]);

  // Poll the exam session while the profile is open. A recruiter uses this card to decide whether
  // to release a lock, so a stale reading is worse than none — the difference between "held by a
  // browser that is still alive" and "held by one that has gone quiet" flips within seconds, and
  // the poll interval is deliberately shorter than the server's staleness window.
  useEffect(() => {
    if (!candidateId || !token) return;
    let cancelled = false;

    const load = async () => {
      const data = await getCandidateExamSessions(candidateId, token);
      if (!cancelled) setExamSessions(data);
    };

    load();
    const interval = setInterval(load, 10000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [candidateId, token]);

  const liveExamSession = examSessions?.sessions?.find((s) => s.isLive) ?? null;

  const handleEndExamSession = async () => {
    if (!candidateId || !token) return;
    setIsEndingSession(true);
    try {
      const count = await revokeCandidateExamSessions(candidateId, token);
      if (count > 0) {
        success("Session ended", "The candidate can now start on the device in front of you.");
      } else {
        info("Nothing to end", "This candidate has no live assessment session.");
      }
      const updated = await getCandidateExamSessions(candidateId, token);
      setExamSessions(updated);
    } catch (err) {
      error("Error", "Failed to end the assessment session. Please try again.");
    } finally {
      setIsEndingSession(false);
    }
  };

  const handleResetAssessmentWindow = async () => {
    if (!activeAssessmentSlot || !candidateId || !token) return;
    setIsResettingWindow(true);
    setWindowResetSuccess(false);
    try {
      await resetCandidateWindow(
        candidateId,
        activeAssessmentSlot.assessmentSlotId,
        token
      );
      setWindowResetSuccess(true);
      // Refresh slot info so the card shows the updated end time
      const updated = await getActiveSlotForCandidate(candidateId, token, roleId);
      setActiveAssessmentSlot(updated);
    } catch (err) {
      error('Error', 'Failed to reset assessment window. Please try again.');
    } finally {
      setIsResettingWindow(false);
    }
  };

  const handleResetJourneyConfirm = async () => {
    if (!candidateId || !roleId || !token) return;
    try {
      await ResetCandidateJourney(candidateId, roleId, token);
      info("Success", "Candidate journey reset to Applied successfully.");
      
      // Refresh candidate data to reflect the reset
      setCandidateStatus([...candidateStatus]); // trigger re-fetch in useEffect
      refreshRoleCandidateCache(roleId, token);
    } catch (err: any) {
      error("Error", err.message || "Failed to reset candidate journey.");
    }
  };

  const handleSendWithoutSeb = async () => {
    if (!activeAssessmentSlot || !candidateId || !token) return;
    setIsSendingWithoutSeb(true);
    setSentWithoutSebSuccess(false);
    try {
      await sendAssessmentLinkWithoutSeb(
        candidateId,
        activeAssessmentSlot.assessmentSlotId,
        token
      );
      setSentWithoutSebSuccess(true);
      // Refresh slot so takenWithoutSeb flag reflects in ScoreGrades badge
      const updated = await getActiveSlotForCandidate(candidateId, token, roleId);
      setActiveAssessmentSlot(updated);
    } catch (err) {
      error('Error', 'Failed to send assessment link. Please try again.');
    } finally {
      setIsSendingWithoutSeb(false);
    }
  };

  // The reassign endpoint cancels the old booking, clears the score and mails the new slot, so the
  // only thing left to do here is re-read the slot that the rest of this card renders from.
  const handleRebooked = async () => {
    if (!candidateId || !token) return;
    setRebookSuccess(true);
    setWindowResetSuccess(false);
    // A fresh booking starts with TakenWithoutSeb = false, so clear the sent-link confirmation too
    // rather than leaving a stale "link sent" message next to a slot that never received one.
    setSentWithoutSebSuccess(false);
    const updated = await getActiveSlotForCandidate(candidateId, token, roleId);
    setActiveAssessmentSlot(updated);
  };

  return (
    <>
      <div className="min-h-full bg-gray-100 dark:bg-gray-900">
        <main className="pb-10">
          {/* Header Section */}
          <div className="bg-gray-100 dark:bg-gray-900 py-6 border-gray-200 dark:border-gray-700 flex flex-col md:flex-row mx-auto max-w-3xl lg:max-w-7xl md:items-center justify-between px-4 sm:px-0">
            <nav
              className="flex items-center space-x-5 mb-4 md:mb-0"
              aria-label="Breadcrumb"
            >
              <ol className="flex items-center sm:space-x-4">
                {location.pathname.includes("/roles") ? (
                  <>
                    <li>
                      <div>
                        <Link
                          to={"/roles"}
                          className="text-sm font-medium text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                        >
                          Roles
                        </Link>
                      </div>
                    </li>
                    <li>
                      <div className="flex items-center">
                        <ChevronRightIcon
                          className="h-5 w-5 flex-shrink-0 text-gray-400"
                          aria-hidden="true"
                        />
                        <Link
                          to={`/roles/${candidateRole[0]?.id}`}
                          className="sm:ml-4 text-sm font-medium text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                        >
                          {candidateRole[0]?.title}
                        </Link>
                      </div>
                    </li>
                    <li>
                      <div className="flex items-center">
                        <ChevronRightIcon
                          className="h-5 w-5 flex-shrink-0 text-gray-400"
                          aria-hidden="true"
                        />
                        <Link
                          to={`/roles/${candidateRole[0]?.id}/candidates/${candidateId}`}
                          className="sm:ml-4 text-sm font-medium text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                        >
                          {candidate?.firstName}
                        </Link>
                      </div>
                    </li>
                  </>
                ) : (
                  <>
                    <li>
                      <div>
                        <Link
                          to={`/roles/${candidateRole[0]?.id}/candidates/${candidateId}`}
                          className="text-sm font-medium text-gray-600 hover:text-gray-700 dark:text-gray-400"
                        >
                          {candidate?.firstName}
                        </Link>
                      </div>
                    </li>
                  </>
                )}
              </ol>
            </nav>
            <button
              onClick={() => setIsResetJourneyModalOpen(true)}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 sm:w-auto"
            >
              Reset Candidate
            </button>
            {/* <div className="mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 text-entntblue hover:bg-blue-50 dark:hover:bg-blue-900/20 px-3 py-2 rounded-lg transition-all font-medium"
              >
                <ArrowLeftIcon className="h-5 w-5" />
                <span>Back</span>
              </button>
            </div> */}
          </div>

          {/* Main Content */}
          <div className="mx-auto grid max-w-3xl grid-cols-1 gap-6 px-4 sm:px-0 lg:max-w-7xl lg:grid-flow-row-dense lg:grid-cols-3 py-8 sm:pt-0">
            <div className="lg:col-span-3 lg:grid lg:grid-cols-3 lg:col-start-1 lg:gap-6 space-y-6 lg:space-y-0">
              <div className="lg:col-span-2 space-y-6">
                <CandidateDetails
                  candidate={candidate}
                  candidateRole={candidateRole}
                  disqualifiedOrHired={disqualifiedOrHired}
                  advanceStatusSpinner={advanceStatusSpinner}
                  setDisqualifyConfirmModal={setDisqualifyConfirmModal}
                  selectedAssessment={selectedAssessment}
                  setSelectedAssessment={setSelectedAssessment}
                  interviewAssessments={interviewAssessments}
                  advanceWithEmail={advanceWithEmail}
                  setAdvanceWithEmail={setAdvanceWithEmail}
                  disqualifyWithEmail={disqualifyWithEmail}
                  setDisqualifyWithEmail={setDisqualifyWithEmail}
                  setAdvanceStatusConfirmModal={setAdvanceStatusConfirmModal}
                  includeSlot={includeSlot}
                  setIncludeSlot={setIncludeSlot}
                  availableSlots={availableSlots}
                  timeline={timeline}
                  nextTimelineIndex={nextTimelineIndex}
                  handleDisqualify={handleDisqualify}
                  handleAdvanceToNextStatus={handleAdvanceToNextStatus}
                  roleStatus={roleStatus}
                  cvFlagResult={cvFlagResult}
                  isFetchingCvFlags={isFetchingCvFlags}
                  cvFlagError={cvFlagError}
                  handleGetCvFlags={handleGetCvFlags}
                />
                <div className="mt-4 w-full">
                  <CandidateNotes candidateId={candidateId} roleId={roleId} />
                </div>
              </div>
              <section
                aria-labelledby="timeline-title"
                className="lg:col-span-1 lg:col-start-3 space-y-6"
              >
                {interviewId && (
                  <div
                    onClick={() =>
                      setIsInterviewQuestionModalOpen((prev) => !prev)
                    }
                    className="inline-flex w-full items-center rounded-md bg-entntblue p-2 hover:bg-entntorange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-entntblue focus-visible:ring-offset-2 focus-visible:ring-offset-gray-50 text-center text-white mb-4 cursor-pointer"
                  >
                    Set Interview Questions
                  </div>
                )}
                {interviewId && (
                  <div
                    className="inline-flex w-full items-center rounded-md bg-entntblue p-2 hover:bg-entntorange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-entntblue focus-visible:ring-offset-2 focus-visible:ring-offset-gray-50 text-center text-white mb-4 cursor-pointer"
                    onClick={() => {
                      const interviewUrl = `${AssessmentUrl}/interview/${interviewId}`;

                    
                      navigator.clipboard.writeText(interviewUrl);
                      setShowCopyLinkNotification(true);
                    }}
                  >
                    Copy Candidate Interview Link
                  </div>
                )}
                {interviewId && (
                  <div
                    className="inline-flex w-full items-center rounded-md bg-entntblue p-2 hover:bg-entntorange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-entntblue focus-visible:ring-offset-2 focus-visible:ring-offset-gray-50 text-center text-white mb-4 cursor-pointer"
                    onClick={() => {
                      setCandidateInterviewScoreOpen(true);
                    }}
                  >
                    See Candidate Interivew Score
                  </div>
                )}
                {isInterviewQuestionModalOpen && activeInterviewId && (
                  <InterviewControlPanel
                    interviewId={activeInterviewId}
                    candidateId={candidateId!}
                    roleId={roleId!}
                    onClose={() => setIsInterviewQuestionModalOpen(false)}
                  />
                )}
                {candidateInterviewScoreOpen && activeInterviewId && (
                  <InterviewScorePanel
                    interviewSession={interviewSession}
                    onClose={() => setCandidateInterviewScoreOpen(false)}
                  />
                )}

                <div className="bg-white dark:bg-darkbglight rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-start justify-between flex-col">
                    <h2
                      id="timeline-title"
                      className="text-base font-semibold text-gray-900 dark:text-gray-100"
                    >
                      Timeline
                    </h2>
                  </div>

                  {/* Timeline list */}
                  <div className="px-6 py-5">
                    {timeline.length === 0 ? (
                      <p className="text-sm text-center text-gray-400 py-6">
                        No timeline events yet
                      </p>
                    ) : (
                      <ul className="space-y-0">
                        {
                          // timeline?.find(t => t.content === "Disqualified") ? timeline.slice(0, timeline.indexOf(timeline.find(t => t.content === "Disqualified"))) : 
                          timeline
                            .slice(0, disqualifiedIndex !== -1 ? disqualifiedIndex + 1 : timeline.length)
                            .map((item, itemIdx) => {
                              const typeKey =
                                typeof item.type === "string"
                                  ? item.type
                                  : (item.type as any)?.name;
                              const typeMetadata = eventTypes[typeKey] || {};
                              const IconComponent = iconMap[typeMetadata.icon];
                              const isCompleted = typeMetadata.icon === "CheckIcon";
                              const isRejected = typeMetadata.icon === "XMarkIcon";
                              const isDefault = !isCompleted && !isRejected;
                              const isLast = itemIdx === timeline.length - 1;

                              return (
                                <li key={item.id} className="relative flex gap-4">
                                  {/* Icon + connector */}
                                  <div className="flex flex-col items-center flex-shrink-0">
                                    <span
                                      className={classNames(
                                        typeMetadata.bgColorClass ||
                                        "bg-gray-300 dark:bg-gray-600",
                                        "h-9 w-9 rounded-full flex items-center justify-center shadow-sm z-10 ring-4",
                                        isCompleted
                                          ? "ring-green-50 dark:ring-green-900/20"
                                          : isDefault
                                            ? "ring-blue-50 dark:ring-blue-900/20"
                                            : "ring-gray-100 dark:ring-gray-800",
                                      )}
                                    >
                                      {IconComponent && (
                                        <IconComponent
                                          className="h-4 w-4 text-white"
                                          aria-hidden="true"
                                        />
                                      )}
                                    </span>
                                    {(!isLast && itemIdx != disqualifiedIndex) && (
                                      <div
                                        className={classNames(
                                          "w-0.5 flex-1 min-h-[2rem] mt-1 mb-1",
                                          isCompleted || isDefault
                                            ? "bg-entntorange/40"
                                            : "bg-gray-200 dark:bg-gray-700",
                                        )}
                                      />
                                    )}
                                  </div>

                                  {/* Content card */}
                                  <div
                                    className={classNames(
                                      "flex-1",
                                      isLast ? "pb-0" : "pb-4",
                                    )}
                                  >
                                    <div
                                      className={classNames(
                                        "rounded-xl px-4 py-3 transition-all  border-gray-200 dark:border-gray-700 opacity-60",
                                      )}
                                    >
                                      <div className="flex items-start justify-between gap-2 -mt-1">
                                        <div className="min-w-0">
                                          <p
                                            className={classNames(
                                              "text-sm font-semibold leading-snug",
                                              isCompleted || isDefault
                                                ? "text-gray-900 dark:text-gray-100"
                                                : "text-gray-500 dark:text-gray-400",
                                            )}
                                          >
                                            {item.content}
                                            {item.target && (
                                              <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">
                                                {item.target}
                                              </span>
                                            )}
                                          </p>
                                        </div>

                                        {/* Badge */}

                                        {item.date ? (
                                          <p className="text-xs text-gray-600 dark:text-gray-500 mt-0.5">
                                            {format(new Date(item.date), "dd MMM yyyy")}
                                          </p>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                </li>
                              );
                            })}
                      </ul>
                    )}
                  </div>
                </div>
                {/* Assessment Window Reset Card — visible only when candidate has an active slot */}
                {activeAssessmentSlot && (
                  <div className="mt-4 bg-white dark:bg-darkbglight rounded-xl shadow-sm border border-amber-100 dark:border-amber-900/40 p-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                      <svg className="h-4 w-4 text-amber-500" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                      </svg>
                      Assessment Window
                    </h3>

                    <div className="space-y-1 mb-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Original end:{" "}
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {new Date(activeAssessmentSlot.originalEndTime).toLocaleString()}
                        </span>
                      </p>
                      {activeAssessmentSlot.windowWasReset && (
                        <p className="text-xs text-blue-600 dark:text-blue-400">
                          Extended end:{" "}
                          <span className="font-medium">
                            {new Date(activeAssessmentSlot.endTime).toLocaleString()}
                          </span>
                        </p>
                      )}
                    </div>

                    <div className="flex gap-2 mb-3">
                      <button
                        id="copy-direct-link-btn"
                        type="button"
                        disabled={!activeAssessmentSlot.directLink}
                        onClick={() => {
                          navigator.clipboard.writeText(activeAssessmentSlot.directLink!);
                          success("Copied", "Direct assessment link copied to clipboard.");
                        }}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                                   text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200
                                   dark:text-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700 dark:border-gray-700
                                   disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5A3.375 3.375 0 0 0 6.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0 0 15 2.25h-1.5a2.251 2.251 0 0 0-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5h-1.5C4.784 7.5 4.5 7.784 4.5 8.25v10.5c0 .466.284.75.75.75h6" />
                        </svg>
                        Copy Direct Link
                      </button>
                      <button
                        id="copy-seb-link-btn"
                        type="button"
                        disabled={!activeAssessmentSlot.sebLink}
                        onClick={() => {
                          navigator.clipboard.writeText(activeAssessmentSlot.sebLink!);
                          success("Copied", "SEB assessment link copied to clipboard.");
                        }}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                                   text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200
                                   dark:text-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700 dark:border-gray-700
                                   disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5A3.375 3.375 0 0 0 6.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0 0 15 2.25h-1.5a2.251 2.251 0 0 0-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5h-1.5C4.784 7.5 4.5 7.784 4.5 8.25v10.5c0 .466.284.75.75.75h6" />
                        </svg>
                        Copy SEB Link
                      </button>
                    </div>

                    {windowResetSuccess && (
                      <div className="flex items-center gap-1.5 text-xs text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg px-3 py-2 mb-3">
                        <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                        Window reset — candidate has a fresh window.
                      </div>
                    )}

                    <button
                      id="reset-assessment-window-btn"
                      onClick={handleResetAssessmentWindow}
                      disabled={isResettingWindow}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                                 text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200
                                 dark:text-amber-300 dark:bg-amber-900/20 dark:hover:bg-amber-900/40 dark:border-amber-800
                                 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                    >
                      {isResettingWindow ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                          </svg>
                          Resetting...
                        </span>
                      ) : (
                        <>
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round"
                              d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                          </svg>
                          Reset Assessment Window
                        </>
                      )}
                    </button>

                    {/* Send Assessment Link Without SEB */}
                    <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                      {sentWithoutSebSuccess && (
                        <div className="flex items-center gap-1.5 text-xs text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg px-3 py-2 mb-2">
                          <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                          </svg>
                          Link sent — no SEB required.
                        </div>
                      )}
                      <button
                        id="send-without-seb-btn"
                        onClick={handleSendWithoutSeb}
                        disabled={isSendingWithoutSeb}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                                   text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200
                                   dark:text-indigo-300 dark:bg-indigo-900/20 dark:hover:bg-indigo-900/40 dark:border-indigo-800
                                   disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                      >
                        {isSendingWithoutSeb ? (
                          <span className="flex items-center gap-2">
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                            </svg>
                            Sending...
                          </span>
                        ) : (
                          <>
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                            </svg>
                            {activeAssessmentSlot?.takenWithoutSeb
                              ? "Resend Link (No SEB)"
                              : "Send Assessment Link Without SEB"}
                          </>
                        )}
                      </button>
                    </div>

                    {/* Book a different slot for the same assessment, replacing this booking */}
                    <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                      {rebookSuccess && (
                        <div className="flex items-center gap-1.5 text-xs text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg px-3 py-2 mb-2">
                          <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                          </svg>
                          Slot replaced — candidate emailed the new details.
                        </div>
                      )}
                      <button
                        id="rebook-assessment-slot-btn"
                        onClick={() => {
                          setRebookSuccess(false);
                          setIsRebookSlotModalOpen(true);
                        }}
                        disabled={!activeAssessmentSlot?.roleStatusId}
                        title={
                          activeAssessmentSlot?.roleStatusId
                            ? undefined
                            : "This booking has no role stage on record, so slots cannot be listed"
                        }
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                                   text-sky-700 bg-sky-50 hover:bg-sky-100 border border-sky-200
                                   dark:text-sky-300 dark:bg-sky-900/20 dark:hover:bg-sky-900/40 dark:border-sky-800
                                   disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.5V7.5A2.25 2.25 0 0 1 5.25 5.25h13.5A2.25 2.25 0 0 1 21 7.5v11a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18.5Zm3.75-6.75h10.5" />
                        </svg>
                        Book Different Slot
                      </button>
                    </div>
                  </div>
                )}

                {/* Live Exam Session Card — only rendered once the candidate has ever claimed a
                    session, so it stays out of the way for candidates who have not sat the test.
                    Hidden entirely when enforcement is Off, where the lock is not in play and the
                    card would imply a guarantee that is not being made. */}
                {examSessions &&
                  examSessions.enforcementMode !== "Off" &&
                  examSessions.sessions.length > 0 && (
                    <div className="mt-4 bg-white dark:bg-darkbglight rounded-xl shadow-sm border border-sky-100 dark:border-sky-900/40 p-4">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                        <svg className="h-4 w-4 text-sky-500" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25m18 0A2.25 2.25 0 0 0 18.75 3H5.25A2.25 2.25 0 0 0 3 5.25m18 0V12a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 12V5.25" />
                        </svg>
                        Exam Session
                      </h3>

                      {liveExamSession ? (
                        <div className="space-y-1 mb-3">
                          <p className="text-xs flex items-center gap-1.5">
                            <span
                              className={`inline-block h-2 w-2 rounded-full ${
                                liveExamSession.isStale ? "bg-amber-400" : "bg-green-500 animate-pulse"
                              }`}
                            />
                            <span className="font-medium text-gray-700 dark:text-gray-300">
                              {liveExamSession.isStale
                                ? "Held, but not responding"
                                : "Active right now"}
                            </span>
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Started:{" "}
                            <span className="font-medium text-gray-700 dark:text-gray-300">
                              {new Date(liveExamSession.claimedAt.endsWith("Z") ? liveExamSession.claimedAt : liveExamSession.claimedAt + "Z").toLocaleString()}
                            </span>
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Last seen:{" "}
                            <span className="font-medium text-gray-700 dark:text-gray-300">
                              {new Date(liveExamSession.lastHeartbeatAt.endsWith("Z") ? liveExamSession.lastHeartbeatAt : liveExamSession.lastHeartbeatAt + "Z").toLocaleTimeString()}
                            </span>
                          </p>
                          {liveExamSession.ipAddress && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              IP:{" "}
                              <span className="font-mono text-gray-700 dark:text-gray-300">
                                {liveExamSession.ipAddress}
                              </span>
                            </p>
                          )}
                          {liveExamSession.isStale && (
                            <p className="text-xs text-amber-700 dark:text-amber-400">
                              This browser has stopped reporting — the next device to start will
                              take it over automatically. Ending it manually is optional.
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                          No live session. The candidate can start on any device.
                        </p>
                      )}

                      {/* The signal worth escalating: a second browser tried to open this
                          assessment while the first was demonstrably still alive. Takeovers are
                          shown separately because they are usually just a flaky connection. */}
                      {examSessions.sessions[0]?.conflictCount > 0 && (
                        <div className="flex items-start gap-1.5 text-xs text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2 mb-3">
                          <svg className="h-4 w-4 flex-shrink-0 mt-px" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                          </svg>
                          <span>
                            <strong>
                              {examSessions.sessions[0].conflictCount} blocked attempt
                              {examSessions.sessions[0].conflictCount === 1 ? "" : "s"}
                            </strong>{" "}
                            to open this assessment from a second browser while one was already
                            active.
                          </span>
                        </div>
                      )}

                      {examSessions.sessions[0]?.takeoverCount > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                          {examSessions.sessions[0].takeoverCount} reconnection
                          {examSessions.sessions[0].takeoverCount === 1 ? "" : "s"} after a dropped
                          browser (usually a network issue, not a concern).
                        </p>
                      )}

                      <button
                        id="end-exam-session-btn"
                        onClick={handleEndExamSession}
                        disabled={isEndingSession || !liveExamSession}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                                   text-red-700 bg-red-50 hover:bg-red-100 border border-red-200
                                   dark:text-red-300 dark:bg-red-900/20 dark:hover:bg-red-900/40 dark:border-red-800
                                   disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                      >
                        {isEndingSession ? (
                          <span className="flex items-center gap-2">
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                            </svg>
                            Ending...
                          </span>
                        ) : (
                          <>
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5.636 5.636a9 9 0 1 0 12.728 0M12 3v9" />
                            </svg>
                            End Active Session
                          </>
                        )}
                      </button>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 text-center">
                        Signs out the browser holding this assessment so the candidate in front of
                        you can start. That window will not reconnect on its own.
                      </p>
                    </div>
                  )}

                <div className="mt-6">
                  {/* Structured-evaluation detail now lives inline in ScoreGrades, as a
                      badge next to the manual score it produced, rather than as a
                      separate section below. */}
                  <ScoreGrades
                    candidateId={candidateId}
                    roleId={roleId}
                  />
                </div>
                <div className="mt-6">
                  <Interviews
                    interviewAssessments={interviewAssessments}
                    selectedAssessment={selectedAssessment}
                    setSelectedAssessment={setSelectedAssessment}
                    isInterviewQuestionModalOpen={isInterviewQuestionModalOpen}
                    setIsInterviewQuestionModalOpen={
                      setIsInterviewQuestionModalOpen
                    }
                    candidateInterviewScoreOpen={candidateInterviewScoreOpen}
                    setCandidateInterviewScoreOpen={
                      setCandidateInterviewScoreOpen
                    }
                    activeInterviewId={activeInterviewId}
                    setActiveInterviewId={setActiveInterviewId}
                    interviewSession={interviewSession}
                    setInterviewSession={setInterviewSession}
                    setShowCopyLinkNotification={setShowCopyLinkNotification}
                  />
                </div>
              </section>
            </div>
          </div>
        </main>
      </div>
      <ConfirmModal
        type="danger"
        open={disqualifyConfirmModal}
        setOpen={setDisqualifyConfirmModal}
        onConfirm={handleDisqualify}
        title="Disqualify Candidate"
        description="Are you sure you want to disqualify this candidate? This action cannot be undone."
      />
      <ResetJourneyModal
        isOpen={isResetJourneyModalOpen}
        onClose={() => setIsResetJourneyModalOpen(false)}
        onConfirm={handleResetJourneyConfirm}
        candidateName={candidate?.firstName || "Candidate"}
        eligibilityData={resetJourneyEligibility}
      />
      {isRebookSlotModalOpen && activeAssessmentSlot?.roleStatusId && candidateId && (
        <RebookAssessmentSlotModal
          isOpen={isRebookSlotModalOpen}
          onClose={() => setIsRebookSlotModalOpen(false)}
          candidateId={candidateId}
          candidateName={candidate?.firstName || "Candidate"}
          currentSlotId={activeAssessmentSlot.assessmentSlotId}
          currentSlotStartTime={activeAssessmentSlot.startTime}
          roleStatusId={activeAssessmentSlot.roleStatusId}
          assessmentQuestionnaireId={activeAssessmentSlot.assessmentQuestionnaireId ?? null}
          assessmentName={activeAssessmentSlot.assessmentName ?? null}
          onRebooked={handleRebooked}
        />
      )}
      <ConfirmModal
        type="info"
        open={advanceStatusConfirmModal}
        setOpen={setAdvanceStatusConfirmModal}
        onConfirm={handleAdvanceToNextStatus}
        title="Advance to Next Round"
        description={
          <div className="space-y-3 text-left">
            <p className="text-gray-700 dark:text-gray-300 text-sm">
              Candidate will be advanced to the next round of the recruitment process.
            </p>

            {hasDetailedSlots && advanceWithEmail === advanceWithEmailOptions[0] && (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gradient-to-br from-gray-50 to-white dark:from-gray-800/60 dark:to-gray-800/20 p-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                  Slot Assignment Mode
                </h4>
                <div className="inline-flex w-full rounded-full bg-gray-100 dark:bg-gray-900/50 p-1">
                  <button
                    type="button"
                    onClick={() => {
                      setAssignMode("candidate");
                      setSelectedAssessmentSlotId("");
                      setSelectedInterviewSlotId("");
                    }}
                    className={`flex-1 rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                      assignMode === "candidate"
                        ? "bg-white dark:bg-gray-700 text-entntblue shadow-sm"
                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    }`}
                  >
                    Let candidate choose
                  </button>
                  <button
                    type="button"
                    onClick={() => setAssignMode("recruiter")}
                    className={`flex-1 rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                      assignMode === "recruiter"
                        ? "bg-white dark:bg-gray-700 text-entntblue shadow-sm"
                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    }`}
                  >
                    Assign a slot now
                  </button>
                </div>
                <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
                  {assignMode === "candidate"
                    ? "Candidate receives a booking link and picks their own slot."
                    : "Candidate receives a single email with their slot already booked."}
                </p>
              </div>
            )}

            {isEmailAdvance && assignMode === "recruiter" && needsAssessmentSlot && (
              <div>
                <h4 className="flex items-center gap-1.5 font-semibold text-xs text-gray-800 dark:text-gray-100 mb-2">
                  <CalendarDaysIcon className="h-4 w-4 text-gray-400" />
                  Select Assessment Slot
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {detailedAssessmentSlots
                    .filter((s) => s.roleStatusId === targetRoleStatusId)
                    .map((slot) => {
                      const active = selectedAssessmentSlotId === slot.id;
                      return (
                        <button
                          type="button"
                          key={slot.id}
                          onClick={() => setSelectedAssessmentSlotId(slot.id)}
                          className={`text-left rounded-lg border p-2.5 transition-all ${
                            active
                              ? "border-entntblue ring-1 ring-entntblue/40 bg-blue-50 dark:bg-blue-900/20"
                              : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800/40"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <span className="text-xs font-semibold text-gray-800 dark:text-gray-100">
                              {new Date(slot.startTime).toLocaleString()} - {new Date(slot.endTime).toLocaleTimeString()}
                            </span>
                            {active && <CheckCircleIcon className="h-4 w-4 text-entntblue flex-shrink-0" />}
                          </div>
                          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                            {slot.assessmentName || "Assessment"}
                          </p>
                          <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                            <UserGroupIcon className="h-3 w-3" />
                            {slot.currentCandidateCount} booked
                          </span>
                        </button>
                      );
                    })}
                </div>
              </div>
            )}

            {isEmailAdvance && assignMode === "recruiter" && !needsAssessmentSlot && matchingInterviewSlotsForNext.length > 0 && (
              <div>
                <h4 className="flex items-center gap-1.5 font-semibold text-xs text-gray-800 dark:text-gray-100 mb-2">
                  <CalendarDaysIcon className="h-4 w-4 text-gray-400" />
                  Select Interview Slot
                </h4>
                <select
                  className="text-xs border border-gray-200 dark:border-gray-700 dark:bg-gray-800 rounded-md w-full py-1.5 px-2 focus:ring-1 focus:ring-entntblue focus:border-entntblue"
                  value={selectedInterviewSlotId}
                  onChange={(e) => setSelectedInterviewSlotId(e.target.value)}
                >
                  <option value="">Select a slot</option>
                  {matchingInterviewSlotsForNext.map((slot: any) => (
                    <option key={slot.id} value={slot.id}>
                      {new Date(slot.startTime).toLocaleString()} - {new Date(slot.endTime).toLocaleTimeString()}
                      {slot.interviewerEmail ? ` (${slot.interviewerEmail})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {isEmailAdvance && isAdvancingToInterview && assignMode === "candidate" && panelGroups.length > 0 && includeSlot && (
              <div>
                <h4 className="flex items-center gap-1.5 font-semibold text-xs text-gray-800 dark:text-gray-100 mb-2">
                  <UserGroupIcon className="h-4 w-4 text-gray-400" />
                  Select Interview Panel
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {panelGroups.map((group) => {
                    const active = selectedPanels.includes(group.panel);
                    return (
                      <button
                        type="button"
                        key={group.panel}
                        onClick={() => {
                          setSelectedPanels((prev) =>
                            prev.includes(group.panel)
                              ? prev.filter((p) => p !== group.panel)
                              : [...prev, group.panel]
                          );
                        }}
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium border transition-colors ${
                          active
                            ? "bg-blue-50 dark:bg-blue-900/30 border-entntblue/40 text-entntblue"
                            : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300"
                        }`}
                      >
                        {active && <CheckCircleIcon className="h-3.5 w-3.5" />}
                        {group.panel}
                        <span className="opacity-60">· {group.count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {isEmailAdvance && isAdvancingToInterview && assignMode === "candidate" && (
              availableSlots.filter((slot) => slot.roleStatusId === targetRoleStatusId).length > 0 ? (
                <div>
                  <h4 className="flex items-center gap-1.5 font-semibold text-xs text-gray-800 dark:text-gray-100 mb-2">
                    <CalendarDaysIcon className="h-4 w-4 text-gray-400" />
                    Available Slots
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {availableSlots
                      .filter((slot) => slot.roleStatusId === targetRoleStatusId)
                      .map((slot) => (
                        <div
                          key={slot.roleStatusId}
                          className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-3 py-2 text-xs"
                        >
                          <p className="font-medium text-gray-700 dark:text-gray-200">{slot.roleStatusName}</p>
                          <p className="text-gray-500 dark:text-gray-400 mt-0.5">{slot.adjustedCount} available</p>
                        </div>
                      ))}
                  </div>
                </div>
              ) : (
                includeSlot && (
                  <div className="flex flex-col items-center gap-1 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 py-3 text-gray-400">
                    <InboxIcon className="h-5 w-5" />
                    <p className="text-xs">No available slots found.</p>
                  </div>
                )
              )
            )}

            {isAdvancingToInterview && assignMode === "candidate" && advanceWithEmail === advanceWithEmailOptions[0] && (
              <div className="flex items-center justify-between gap-3 bg-blue-50 dark:bg-blue-900/20 px-3 py-2.5 rounded-lg">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Send Interview Slots to Candidate
                </span>
                <ToggleSwitch checked={includeSlot} onChange={handleToggleSlot} />
              </div>
            )}
          </div>
        }
      />
    </>
  );
}
